import './coach.css';
import { escapeHtml } from '../lib/ui';
import { toast } from '../lib/ui';
import {
  sendCoachMessage,
  fetchChatHistory,
  saveChatMessage,
  clearChat,
  type ChatMessage,
} from '../services/coach';

// טאב המאמנת — צ'אט AI. הודעת פתיחה חמה (אפמרלית, לא נשמרת ולא נשלחת למודל),
// בועות משתמשת/מאמנת, אינדיקציית הקלדה, שמירה/טעינה מ-litebite_chat.

// בלי פניה בגוף שני מוטה-מגדר (הלקוח לא יודע את המין כאן) — "אותך"/"לך" זהים לזכר ולנקבה.
const GREETING =
  'היי! 🌿 כיף לראות אותך. אני המאמנת שלך — מכירה את היעדים והיומן שלך, אז אפשר להתייעץ ' +
  'איתי על אוכל, כמויות, או פשוט לקבל דחיפה קטנה. על מה בא לך לדבר?';

// מצב בזיכרון — שורד מעבר בין טאבים (ה-DOM נבנה מחדש, המצב נשמר).
let messages: ChatMessage[] = [];
let historyLoaded = false;
let sending = false;
let typing = false;
let errorNote: string | null = null;

// ---------- עזרי DOM (מאתרים את ה-DOM החי לפי id — עמיד לרינדור מחדש) ----------

function bubblesEl(): HTMLElement | null {
  return document.getElementById('coach-bubbles');
}
function inputEl(): HTMLInputElement | null {
  return document.getElementById('coach-text') as HTMLInputElement | null;
}

function bubbleHtml(role: 'user' | 'coach', content: string, extra = ''): string {
  const html = escapeHtml(content).replace(/\n/g, '<br>');
  return `<div class="bubble ${role}${extra}">${html}</div>`;
}

// חץ שליחה מצויר במפורש שמאלה — לא תו יוניקוד: "➤" לא מתהפך ע"י ה-bidi ומצביע ימינה
// גם כשהכפתור יושב בצד שמאל של שדה RTL (כמו חץ-החזרה ב-addSheet.ts, מאותה סיבה).
const SEND_ARROW_SVG =
  '<svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><line x1="20" y1="12" x2="4" y2="12"/><polyline points="10 6 4 12 10 18"/></svg>';

const TYPING_HTML =
  '<div class="bubble coach typing" aria-label="המאמנת מקלידה"><span></span><span></span><span></span></div>';

function renderBubbles(): void {
  const c = bubblesEl();
  if (!c) return;
  let html = bubbleHtml('coach', GREETING);
  for (const m of messages) html += bubbleHtml(m.role, m.content);
  if (typing) html += TYPING_HTML;
  if (errorNote && !typing) html += bubbleHtml('coach', errorNote, ' err');
  c.innerHTML = html;
}

function scrollBottom(): void {
  requestAnimationFrame(() => {
    window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
  });
}

// ---------- הודעת שגיאה בעברית לפי קוד ----------

function errorMessage(err: unknown): string {
  const code = err instanceof Error ? err.message : '';
  if (code === 'no_session') return 'נראה שהחיבור פג — התחברי מחדש ונמשיך 🙂';
  if (code === 'rate_limited') return 'אני קצת עמוסה ברגע זה 🙈 נסי שוב עוד כמה שניות.';
  if (code === 'forbidden') return 'החשבון עוד לא אושר לשימוש במאמנת.';
  if (code === 'ai_timeout') return 'לקח לי יותר מדי זמן לחשוב… נסי לשלוח שוב 🙏';
  if (code === 'ai_busy') return 'שירות ה-AI של גוגל עמוס כרגע — התקלה אצלם, לא בך 🙈 נסי שוב בעוד כמה דקות.';
  return 'אופס, משהו השתבש אצלי. נסי לשלוח שוב עוד רגע 💚';
}

// ---------- שליחה ----------

async function send(): Promise<void> {
  const input = inputEl();
  const text = input?.value.trim() ?? '';
  if (!text || sending) return;

  sending = true;
  errorNote = null;
  if (input) {
    input.value = '';
    input.disabled = false; // נשאר פתוח כדי לשמור על המקלדת
  }
  toggleSend(true);

  messages.push({ role: 'user', content: text });
  typing = true;
  renderBubbles();
  scrollBottom();
  void saveChatMessage('user', text).catch(() => {});

  try {
    const reply = await sendCoachMessage(messages.slice(-16));
    messages.push({ role: 'coach', content: reply });
    void saveChatMessage('coach', reply).catch(() => {});
  } catch (err) {
    errorNote = errorMessage(err);
  } finally {
    sending = false;
    typing = false;
    toggleSend(false);
    renderBubbles();
    scrollBottom();
    inputEl()?.focus();
  }
}

function toggleSend(disabled: boolean): void {
  const btn = document.getElementById('coach-send') as HTMLButtonElement | null;
  if (btn) btn.disabled = disabled;
}

// ---------- render + attach ----------

export function renderCoach(): string {
  return `
    <div class="coach-screen">
      <div class="card chat-head">
        <div class="coach-avatar">🌿</div>
        <div class="chat-head-txt">
          <div class="t">המאמנת שלך</div>
          <div class="s">● מכירה את היומן שלך</div>
        </div>
        <button type="button" class="chat-clear" id="coach-clear" aria-label="ניקוי שיחה">🗑️</button>
      </div>
      <div class="bubbles" id="coach-bubbles" aria-live="polite"></div>
      <form class="chat-input" id="coach-form">
        <input id="coach-text" type="text" autocomplete="off" enterkeyhint="send" data-kb="text" autocorrect="on" autocapitalize="sentences" spellcheck="true"
          placeholder="כתבי לי מה אכלת או שאלי אותי…" />
        <button type="submit" class="send" id="coach-send" aria-label="שליחה">${SEND_ARROW_SVG}</button>
      </form>
    </div>`;
}

export function attachCoach(root: HTMLElement): void {
  renderBubbles();

  root.querySelector('#coach-form')?.addEventListener('submit', (e) => {
    e.preventDefault();
    void send();
  });

  root.querySelector('#coach-clear')?.addEventListener('click', async () => {
    if (sending) return;
    messages = [];
    errorNote = null;
    renderBubbles();
    try {
      await clearChat();
      toast('השיחה נוקתה');
    } catch {
      toast('הניקוי נכשל — נסי שוב', 'error');
    }
  });

  // טעינת היסטוריה פעם אחת; אם ריק/נכשל — נשארים עם הפתיח בלבד.
  if (!historyLoaded) {
    historyLoaded = true;
    void fetchChatHistory()
      .then((hist) => {
        if (hist.length && !messages.length) {
          messages = hist;
          renderBubbles();
          scrollBottom();
        }
      })
      .catch(() => {});
  }
  scrollBottom();
}
